var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { StringUtils } from '@shared/utils/StringUtils';
import { Greeks } from '@shared/commons/cache/OptionMaster/OptionTrader/Greeks';
import { GreeksFormatter } from '@shared/commons/cache/OptionMaster/OptionTrader/GreeksFormatter';
import { Resources } from '@shared/localizations/Resources';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { InfoControl, InfoData, InfoDataItem } from '../../elements/InfoControl';
var GroupType;
(function (GroupType) {
    GroupType[GroupType["Greeks"] = 0] = "Greeks";
})(GroupType || (GroupType = {}));
var ItemType;
(function (ItemType) {
    ItemType[ItemType["Delta"] = 0] = "Delta";
    ItemType[ItemType["Gamma"] = 1] = "Gamma";
    ItemType[ItemType["Vega"] = 2] = "Vega";
    ItemType[ItemType["Theta"] = 3] = "Theta";
})(ItemType || (ItemType = {}));
export class OptionPortfolioControl extends InfoControl {
    constructor() {
        super();
        this.DEFATULT_STR = StringUtils.THREE_DASHES;
        this.updateDataAsync = this.updateDataAsync.bind(this);
        this.onTimer500msTick = this.onTimer500msTick.bind(this);
        this.updateDataWithoutRequest = this.updateDataWithoutRequest.bind(this);
    }
    getType() {
        return 'OptionPortfolioControl';
    }
    oninit() {
        super.oninit();
        const map = new Map([
            [GroupType.Greeks, [ItemType.Delta, ItemType.Gamma, ItemType.Vega, ItemType.Theta]]
        ]);
        const data = [];
        map.forEach((items, group) => {
            data.push(new InfoData(group.toString(), this.initializeItems(items)));
        });
        void super.set('data', data);
    }
    oncomplete() {
        super.observe('visible', this.onVisibleChanged);
    }
    onteardown() {
        this._optionTrader.unsubscribeOnChangeAccountForPaperPositions(this.updateDataWithoutRequest);
        this._optionTrader.unsubscribeOnChangeInstrumentForPaperPosition(this.updateDataWithoutRequest);
        clearInterval(this._timer500ms);
        super.onteardown();
    }
    setOptionTrader(optionTrader) {
        this._optionTrader = optionTrader;
        this._optionTrader.subscribeOnChangeAccountForPaperPositions(this.updateDataWithoutRequest);
        this._optionTrader.subscribeOnChangeInstrumentForPaperPosition(this.updateDataWithoutRequest);
        clearInterval(this._timer500ms);
        this._timer500ms = setInterval(this.onTimer500msTick, 500);
    }
    updateDataWithoutRequest() {
        void this.updateDataAsync();
    }
    updateDataAsync() {
        const _super = Object.create(null, {
            get: { get: () => super.get },
            update: { get: () => super.update }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const isVisible = yield _super.get.call(this, 'visible');
            if (!isVisible) {
                return;
            }
            yield this.updateGreeksDataAsync();
            yield _super.update.call(this);
        });
    }
    updateGreeksDataAsync() {
        const _super = Object.create(null, {
            get: { get: () => super.get }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const aggregatedGreeks = new Greeks();
            const paperPositions = this._optionTrader.getPaperPositionsForAnalyzer();
            for (let i = 0; i < paperPositions.length; i++) {
                const paperPosition = paperPositions[i];
                if (aggregatedGreeks.isEmpty()) {
                    aggregatedGreeks.delta = 0;
                    aggregatedGreeks.gamma = 0;
                    aggregatedGreeks.vega = 0;
                    aggregatedGreeks.theta = 0;
                }
                const sign = paperPosition.Operation === OperationType.Buy ? 1 : -1;
                const greeks = this._optionTrader.getGreeks(paperPosition.Instrument.PutCall, paperPosition.Instrument, paperPosition.PriceForGreeksCalculation);
                aggregatedGreeks.delta += GreeksFormatter.roundGreek(greeks.delta * paperPosition.QuantityAmount * sign);
                aggregatedGreeks.gamma += GreeksFormatter.roundGreek(greeks.gamma * paperPosition.QuantityAmount * sign);
                aggregatedGreeks.vega += GreeksFormatter.roundGreek(greeks.vega * paperPosition.QuantityAmount * sign);
                aggregatedGreeks.theta += GreeksFormatter.roundGreek(greeks.theta * paperPosition.QuantityAmount * sign);
            }
            const data = yield _super.get.call(this, 'data');
            for (let i = 0; i < data.length; i++) {
                const infoData = data[i];
                if (infoData.id !== GroupType.Greeks.toString()) {
                    continue;
                }
                for (let j = 0; j < infoData.items.length; j++) {
                    const infoDataItem = infoData.items[j];
                    switch (infoDataItem.id) {
                        case ItemType.Delta.toString():
                            infoDataItem.value = GreeksFormatter.formatGreek(aggregatedGreeks.delta);
                            break;
                        case ItemType.Gamma.toString():
                            infoDataItem.value = GreeksFormatter.formatGreek(aggregatedGreeks.gamma);
                            break;
                        case ItemType.Vega.toString():
                            infoDataItem.value = GreeksFormatter.formatGreek(aggregatedGreeks.vega);
                            break;
                        case ItemType.Theta.toString():
                            infoDataItem.value = GreeksFormatter.formatGreek(aggregatedGreeks.theta);
                            break;
                    }
                }
            }
        });
    }
    initializeItems(items) {
        return items.map((item) => {
            return new InfoDataItem(item.toString(), this.getItemTitle(item), this.DEFATULT_STR);
        });
    }
    getItemTitle(item) {
        switch (item) {
            case ItemType.Delta:
                return Resources.getResource('panel.optionPaperPositions.Delta');
            case ItemType.Gamma:
                return Resources.getResource('panel.optionPaperPositions.Gamma');
            case ItemType.Vega:
                return Resources.getResource('panel.optionPaperPositions.Vega');
            case ItemType.Theta:
                return Resources.getResource('panel.optionPaperPositions.Theta');
            default:
                return '';
        }
    }
    onVisibleChanged() {
        const isVisible = super.get('visible');
        if (isVisible) {
            this.updateDataWithoutRequest();
        }
    }
    onTimer500msTick() {
        this.updateDataWithoutRequest();
    }
}
InfoControl.extendWith(OptionPortfolioControl, {
    data: function () {
        return {
            columnsCount: 4,
            rowsCount: 1
        };
    }
});
